import React, { useState, useEffect, useRef } from "react";
import { FiX, FiEdit2 } from "react-icons/fi";
import { marked } from "marked";
import TicketStatusBadge from "../TicketStatusBadge/page";
import { apiClient } from "../../../../api/apiclient";

const Row = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", marginBottom: 14 }}>
    <span style={{
      fontSize: 11, fontWeight: 600, color: "#9ca3af",
      textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3,
    }}>
      {label}
    </span>
    <span style={{ fontSize: 14, color: "#111827", fontWeight: 400 }}>
      {value || "—"}
    </span>
  </div>
);

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days === 1) return `Yesterday ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const groupByDate = (items) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const groups = {};
  (items || []).forEach((item) => {
    const d = new Date(item.createdAt);
    d.setHours(0, 0, 0, 0);
    let label;
    if (d.getTime() === today.getTime()) label = "TODAY";
    else if (d.getTime() === yesterday.getTime()) label = "YESTERDAY";
    else label = "OLDER";
    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  });
  const ordered = [];
  ["TODAY", "YESTERDAY", "OLDER"].forEach((k) => {
    if (groups[k]) ordered.push({ label: k, items: groups[k] });
  });
  return ordered;
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = ["#e0e7ff", "#fce7f3", "#dcfce7", "#fef9c3", "#ffe4e6"];
const getAvatarColor = (name = "") =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

// Tag pill used in activity timeline for status chips
const Pill = ({ value, color = "#f3f4f6", textColor = "#374151" }) => (
  <span style={{
    display: "inline-block", padding: "1px 8px",
    background: color, borderRadius: 4,
    fontSize: 11, color: textColor, fontWeight: 500,
  }}>
    {value}
  </span>
);

// Helper: always reads the freshest token at call-time
const getToken = () => localStorage.getItem("token");

// Helper: fetch + split comments vs activity for a ticket id
// apiClient may already have auth interceptor — we pass header explicitly as a safety net
const fetchComments = async (ticketId) => {
  const res = await apiClient.get(`/tickets/${ticketId}/comments`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const all = Array.isArray(res.data) ? res.data : (res.data?.data || []);
  console.log("[fetchComments] raw response:", res.data, "→ parsed array:", all);
  console.log("[Activity items]", all.filter(a => a.activityType === "REASSIGN"));
  return {
    comments: all.filter((a) => a.activityType === "COMMENT"),
    activity: all.filter((a) => a.activityType !== "COMMENT"),
  };
};

const TicketDetailPanel = ({ ticket, onClose, onEdit, onChangeStatus, onReassign, onRefreshNeeded }) => {
  const myUserId = Number(localStorage.getItem("id"));
  const roleId = localStorage.getItem("roleId");
  const isAdmin = roleId !== "3";
  const myName = localStorage.getItem("userName");
  const canEdit = isAdmin || ticket?.createdBy === myName;
  const [comments, setComments] = useState([]);
  const [activityTimeline, setActivity] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentAttachments, setCommentAttachments] = useState([]);
  const commentFileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [commentTab, setCommentTab] = useState("write"); // "write" | "preview"
  const commentCursorPosRef = useRef({ start: 0, end: 0 });
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch comments + activity whenever the ticket changes
  const loadComments = () => {
    if (!ticket?.id) return;
    fetchComments(ticket.id)
      .then(({ comments, activity }) => {
        setComments(comments);
        setActivity(activity);
      })
      .catch((err) => {
        console.error("Failed to load comments:", err);
      });
  };

  const panelBodyRef = useRef(null);

  useEffect(() => {
    if (!ticket?.id) return;
    setComments([]);
    setActivity([]);
    setCommentError("");
    loadComments();
    // Scroll to top when ticket changes
    if (panelBodyRef.current) panelBodyRef.current.scrollTop = 0;
  }, [ticket?.id]);

  // Expose refresh to parent via callback
  useEffect(() => {
    if (onRefreshNeeded) onRefreshNeeded(loadComments);
  }, [ticket?.id]);

  const handlePostComment = async () => {
    const text = newComment.trim();

    if ((!text && commentAttachments.length === 0) || submitting) {
      return;
    }

    setSubmitting(true);
    setCommentError("");

    // Optimistic update — show the comment immediately
    const optimistic = {
      id: `optimistic-${Date.now()}`,
      activityType: "COMMENT",
      comment: text,
      attachments: [],
      createdAt: new Date().toISOString(),
      user: null,
    };
    setComments((prev) => [...prev, optimistic]);
    setNewComment("");
    const filesToSend = commentAttachments;
    setCommentAttachments([]);

    try {
      const formData = new FormData();
      formData.append("comment", text);
      filesToSend.forEach((f) => formData.append("files", f));

      await apiClient.post(`/tickets/${ticket.id}/comments`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Refetch to get the real comment with user info
      const { comments: fresh, activity } = await fetchComments(ticket.id);
      setComments(fresh);
      setActivity(activity);
    } catch (err) {
      console.error("[Comment] POST failed:", err);
      console.error("[Comment] Error response:", err?.response?.status, err?.response?.data);
      // Roll back optimistic update and restore the typed text
      setComments((prev) => prev.filter((c) => c.id !== optimistic.id));
      setNewComment(text);
      const errMsg = err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || "Failed to post comment. Please try again.";
      setCommentError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Save an edited comment (PUT to the same comments endpoint)
  const handleSaveEditComment = async (commentId) => {
    const text = newComment.trim();
    if (!text) return;

    const realId = String(commentId).replace(/^act-/, "");
    setIsSavingEdit(true);
    setCommentError("");
    try {
      const formData = new FormData();
      formData.append("comment", text);
      commentAttachments.forEach((f) => formData.append("files", f));

      await apiClient.put(`/tickets/${ticket.id}/comments/${realId}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const { comments: fresh, activity } = await fetchComments(ticket.id);
      setComments(fresh);
      setActivity(activity);
      setEditingCommentId(null);
      setNewComment("");
      setCommentAttachments([]);
    } catch (err) {
      console.error("[Comment] Edit failed:", err);
      setCommentError(err?.response?.data?.message || "Failed to update comment");
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Composer button routes to "post new" or "save edit" depending on mode
  const handleComposerSubmit = () => {
    if (editingCommentId) {
      handleSaveEditComment(editingCommentId);
    } else {
      handlePostComment();
    }
  };

  // Wraps/toggles the selected text in the comment box with markdown markers
  const insertAtCommentCursor = (before, after = "") => {
    if (commentTab !== "write") {
      setCommentTab("write");
      setTimeout(() => insertAtCommentCursor(before, after), 0);
      return;
    }
    const ta = document.getElementById("ticket-comment-editor");
    if (!ta) return;
    ta.focus();

    const s = (document.activeElement === ta) ? ta.selectionStart : commentCursorPosRef.current.start;
    const e2 = (document.activeElement === ta) ? ta.selectionEnd : commentCursorPosRef.current.end;
    const text = ta.value;
    const hadSelection = e2 > s;
    const rawSelected = hadSelection ? text.substring(s, e2) : "text";

    const leadingWs = rawSelected.match(/^\s*/)[0];
    const trailingWs = rawSelected.match(/\s*$/)[0];
    const core = rawSelected.slice(
      leadingWs.length,
      trailingWs.length ? rawSelected.length - trailingWs.length : undefined
    ) || rawSelected;

    const coreStart = s + leadingWs.length;
    const coreEnd = e2 - trailingWs.length;
    const alreadyWrapped =
      before && after &&
      text.substring(coreStart - before.length, coreStart) === before &&
      text.substring(coreEnd, coreEnd + after.length) === after;

    let nt, selStart, selEnd;
    if (alreadyWrapped) {
      nt = text.substring(0, coreStart - before.length) + core + text.substring(coreEnd + after.length);
      selStart = coreStart - before.length;
      selEnd = selStart + core.length;
    } else {
      nt = text.substring(0, s) + leadingWs + before + core + after + trailingWs + text.substring(e2);
      selStart = s + leadingWs.length;
      selEnd = selStart + before.length + core.length + after.length;
    }

    commentCursorPosRef.current = { start: selEnd, end: selEnd };
    setNewComment(nt);
    setCommentError("");
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = selStart;
      ta.selectionEnd = selEnd;
    }, 0);
  };

  // Enter continues bullet ("- ") or numbered ("1. ") lists; plain Enter posts
  const handleCommentKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    const ta = e.target;
    const value = ta.value;
    const pos = ta.selectionStart;
    const lastLine = value.substring(0, pos).split("\n").pop();

    if (lastLine.startsWith("- ")) {
      e.preventDefault();
      const nt = value.substring(0, pos) + "\n- " + value.substring(pos);
      setNewComment(nt);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + 3; }, 0);
      return;
    }
    if (/^\d+\.\s?/.test(lastLine)) {
      e.preventDefault();
      const n = Number(lastLine.match(/^(\d+)\.\s?/)[1]) + 1;
      const nt = value.substring(0, pos) + "\n" + n + ". " + value.substring(pos);
      setNewComment(nt);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + (n + ". ").length + 1; }, 0);
      return;
    }
    e.preventDefault();
    handleComposerSubmit();
  };

  // Auto-grow the textarea as the user types
  useEffect(() => {
    const ta = document.getElementById("ticket-comment-editor");
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 240) + "px";
  }, [newComment, commentTab]);

  if (!ticket) return null;
  console.log("ticket.attachments RAW:", JSON.stringify(ticket.attachments));

  return (
    <div style={{
      position: "fixed", top: 0, right: 0,
      width: 450,
      height: "100vh",
      background: "#fff",
      borderLeft: "1px solid #e5e7eb",
      boxShadow: "-2px 0 16px rgba(0,0,0,0.07)",
      zIndex: 1200,
      display: "flex",
      flexDirection: "column",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>

      {/* ── Scrollable body ── */}
      <div ref={panelBodyRef} style={{ flex: 1, overflowY: "auto" }}>

        {/* ── Header ── */}
        <div style={{
          padding: "14px 16px 10px",
          borderBottom: "1px solid #f0f0f0",
          position: "sticky", top: 0, background: "#fff", zIndex: 2,
        }}>
          {/* Ticket number + status badge row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{
              fontSize: 11, fontWeight: 600, color: "#6b7280",
              fontFamily: "monospace", letterSpacing: "0.03em",
            }}>
              {ticket.ticketNumber || "TKT-0000"}
            </span>
            {/* Open badge */}
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: "#3b82f6", background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 20, padding: "1px 10px",
            }}>
              {ticket.status || "Open"}
            </span>
            {/* Close button — top right */}
            <button
              onClick={onClose}
              style={{
                marginLeft: "auto", background: "none", border: "none",
                cursor: "pointer", color: "#6b7280", padding: 2,
                display: "flex", alignItems: "center",
              }}
            >
              <FiX size={16} />
            </button>
          </div>

          {/* Title */}
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
            {ticket.title}
          </div>

          {/* Ticket type badge */}
          {ticket.ticketType && (
            <span style={{
              display: "inline-block",
              fontSize: 11, color: "#6b7280",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              borderRadius: 4, padding: "1px 8px",
            }}>
              {ticket.ticketType}
            </span>
          )}
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "14px 16px" }}>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6,
            }}>
              Description
            </div>
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>
              {ticket.description || "—"}
            </p>
          </div>

          {/* Details */}
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, marginBottom: 16 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12,
            }}>
              Details
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <Row label="Client" value={ticket.client} />
              <Row label="Module" value={ticket.module} />
            </div>
            <Row label="Assignee" value={ticket.assignee || ticket.assigneeName || "—"} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <Row
                label="Created"
                value={
                  ticket.createdAt
                    ? new Date(ticket.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    : "—"
                }
              />
              <Row label="Due" value={ticket.dueDate} />
            </div>
          </div>

          {/* Attachments */}
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, marginBottom: 16 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10,
            }}>
              Attachments
            </div>
            {/* Existing attachments from API */}
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {ticket.attachments.map((att, i) => {
                  const fileName = typeof att === "string" ? att : (att.fileName || att.name || att.originalName || `File ${i + 1}`);
                  const handleDownload = async () => {
                    try {
                      const res = await apiClient.get(`/tickets/attachments/id/${att.id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                        responseType: "blob",
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = fileName;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch {
                      alert("Failed to download attachment");
                    }
                  };
                  return (
                    <button
                      key={i}
                      onClick={handleDownload}
                      style={{
                        background: "#dbeafe", color: "#1d4ed8", borderRadius: 6,
                        fontSize: 11, padding: "2px 10px",
                        display: "flex", alignItems: "center", gap: 6,
                        border: "none", cursor: "pointer", fontFamily: "inherit",
                      }}
                    >
                      📎 {fileName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Comments */}
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, marginBottom: 16 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12,
            }}>
              Comments
            </div>



            {/* Add comment input */}
            <div style={{
              border: `1px solid ${commentError ? "#fca5a5" : "#e5e7eb"}`, borderRadius: 7,
              background: "#f9fafb", overflow: "hidden",
            }}>
              {commentTab === "write" ? (
                <textarea
                  id="ticket-comment-editor"
                  rows={1}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => { setNewComment(e.target.value); setCommentError(""); }}
                  onClick={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                  onKeyUp={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                  onSelect={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                  onBlur={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                  onKeyDown={handleCommentKeyDown}
                  style={{
                    width: "100%", border: "none", outline: "none", resize: "none", overflow: "auto",
                    background: "transparent", fontSize: 13, fontFamily: "inherit", color: "#374151",
                    lineHeight: 1.5, boxSizing: "border-box", padding: "7px 10px", minHeight: 30, maxHeight: 240,
                  }}
                />
              ) : (
                <div
                  style={{ minHeight: 30, fontSize: 13, color: "#374151", padding: "7px 10px" }}
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      try {
                        return marked(newComment || "", { breaks: true });
                      } catch {
                        return (newComment || "").replace(/</g, "&lt;");
                      }
                    })(),
                  }}
                />
              )}

              <div style={{ borderTop: "1px solid #e5e7eb" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "5px 8px" }}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commentFileInputRef.current?.click()}
                  title="Attach file(s)"
                  style={{
                    background: "none", border: "none", padding: "3px 6px",
                    cursor: "pointer", color: commentAttachments.length > 0 ? "#2563eb" : "#9ca3af",
                    borderRadius: 4, lineHeight: 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                <input
                  type="file"
                  multiple
                  ref={commentFileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files || []);
                    if (newFiles.length) setCommentAttachments((prev) => [...prev, ...newFiles]);
                    e.target.value = null;
                  }}
                />

                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertAtCommentCursor("- ")}
                  title="Bullet list"
                  style={{ background: "none", border: "none", padding: "3px 6px", cursor: "pointer", color: "#9ca3af", borderRadius: 4, lineHeight: 1, display: "flex", alignItems: "center" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                </button>

                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertAtCommentCursor("**", "**")}
                  title="Bold"
                  style={{ background: "none", border: "none", padding: "3px 6px", cursor: "pointer", color: "#9ca3af", borderRadius: 4, fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
                  B
                </button>

                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertAtCommentCursor("*", "*")}
                  title="Italic"
                  style={{ background: "none", border: "none", padding: "3px 6px", cursor: "pointer", color: "#9ca3af", borderRadius: 4, fontSize: 12, fontStyle: "italic", lineHeight: 1 }}>
                  I
                </button>

                <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
                  {["write", "preview"].map((t) => (
                    <button key={t} type="button" onClick={() => setCommentTab(t)}
                      style={{
                        background: commentTab === t ? "#e5e7eb" : "none", border: "none", borderRadius: 4,
                        padding: "2px 8px", fontSize: 11, color: commentTab === t ? "#111827" : "#9ca3af",
                        fontWeight: commentTab === t ? 600 : 400, cursor: "pointer",
                      }}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
              {editingCommentId && (
                <button
                  type="button"
                  onClick={() => { setEditingCommentId(null); setNewComment(""); }}
                  style={{
                    background: "none", border: "1px solid #d1d5db", borderRadius: 5,
                    padding: "4px 12px", color: "#374151", fontSize: 11, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleComposerSubmit}
                disabled={(editingCommentId ? isSavingEdit : submitting) || (!newComment.trim() && commentAttachments.length === 0)}
                style={{
                  background: (newComment.trim() || commentAttachments.length > 0) ? "#2563eb" : "#e5e7eb",
                  border: "none", borderRadius: 5, padding: "4px 12px",
                  cursor: (newComment.trim() || commentAttachments.length > 0) && !(editingCommentId ? isSavingEdit : submitting) ? "pointer" : "default",
                  color: (newComment.trim() || commentAttachments.length > 0) ? "#fff" : "#9ca3af",
                  fontSize: 11, fontWeight: 600,
                  opacity: (editingCommentId ? isSavingEdit : submitting) ? 0.6 : 1, transition: "all 0.15s",
                }}
              >
                {editingCommentId
                  ? (isSavingEdit ? "Saving..." : "Save")
                  : (submitting ? "Posting..." : "Post")}
              </button>
            </div>

            {commentAttachments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {commentAttachments.map((f, i) => (
                  <div key={f.name + i} style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: "#eff6ff", border: "1px solid #bfdbfe",
                    borderRadius: 4, padding: "2px 8px", fontSize: 11, color: "#1d4ed8",
                  }}>
                    <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setCommentAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 12, padding: 0 }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            {commentError && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#ef4444" }}>
                {commentError}
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, marginTop: 4 }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: "0.07em", textTransform: "uppercase" }}>
                  Activity Timeline
                </span>
              </div>
              <span style={{ fontSize: 11, color: "#9ca3af" }}>
                {comments.length + activityTimeline.length} update{(comments.length + activityTimeline.length) !== 1 ? "s" : ""}
              </span>
            </div>

            {(comments.length + activityTimeline.length) === 0 ? (
              <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>
                No activity yet
              </div>
            ) : (
              groupByDate(
                [...comments, ...activityTimeline].sort(
                  (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                )
              ).map((group) => (
                <div key={group.label} style={{ marginBottom: 14 }}>
                  {/* Date group label */}
                  <div style={{
                    fontSize: 9, fontWeight: 700, color: "#9ca3af",
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    marginBottom: 8, paddingBottom: 4,
                    borderBottom: "1px solid #f3f4f6",
                  }}>
                    {group.label}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {group.items.map((item, idx) => {
                      const name = item.user
                        ? `${item.user.firstName || ""} ${item.user.lastName || ""}`.trim() || "Unknown"
                        : "Unknown";
                      const isLast = idx === group.items.length - 1;

                      const rawComment = item.comment || item.text || item.content || "";
                      const isMine = item.activityType === "COMMENT" && item.user && Number(item.user.id) === myUserId && !String(item.id).startsWith("optimistic-");
                      const isEditingThis = editingCommentId === item.id;

                      // Status pill color logic
                      const getPillStyle = (val) => {
                        if (!val) return { bg: "#f3f4f6", text: "#374151" };
                        const v = val.toLowerCase();
                        if (v === "resolved") return { bg: "#dcfce7", text: "#16a34a" };
                        if (v === "in progress") return { bg: "#dbeafe", text: "#2563eb" };
                        if (v === "pending review") return { bg: "#ede9fe", text: "#7c3aed" };
                        if (v === "open") return { bg: "#eff6ff", text: "#3b82f6" };
                        if (v === "rejected") return { bg: "#fee2e2", text: "#ef4444" };
                        if (v === "closed") return { bg: "#f3f4f6", text: "#6b7280" };
                        return { bg: "#f3f4f6", text: "#374151" };
                      };

                      const fromStyle = getPillStyle(item.fromValue);
                      const toStyle = getPillStyle(item.toValue);

                      // Human-readable action label
                      const getActionLabel = () => {
                        const type = item.activityType;
                        if (type === "STATUS_CHANGE") return "changed status";
                        if (type === "REASSIGN") return "reassigned ticket to";
                        if (type === "ASSIGNED") return "assigned ticket to";
                        if (type === "CREATED") return "created this ticket";
                        if (type === "UPDATE") return "updated ticket";
                        if (type === "COMMENT") return "commented:";
                        return "updated";
                      };

                      return (
                        <div key={item.id || idx} style={{ display: "flex", gap: 8 }}>
                          {/* Avatar + connector */}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 28 }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: "50%",
                              background: getAvatarColor(name), color: "#3730a3",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 10, fontWeight: 700, zIndex: 1,
                            }}>
                              {getInitials(name)}
                            </div>
                            {!isLast && (
                              <div style={{ width: 1, flex: 1, background: "#e5e7eb", minHeight: 14 }} />
                            )}
                          </div>

                          {/* Content */}
                          <div style={{ paddingBottom: isLast ? 0 : 14, paddingTop: 4, flex: 1 }}>
                            <div style={item.activityType === "COMMENT" ? { background: "#f3f4f6", borderRadius: 8, padding: "8px 12px" } : {}}>
                              <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, flexWrap: "wrap", display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ fontWeight: 600, color: "#111827" }}>{name}</span>
                                <span>{getActionLabel()}</span>
                                {item.activityType === "REASSIGN" ? (
                                  <>
                                    {item.toValue && (
                                      <span style={{
                                        display: "inline-block", padding: "1px 7px",
                                        background: "#f3f4f6", borderRadius: 4,
                                        fontSize: 11, color: "#374151", fontWeight: 500,
                                      }}>
                                        {item.toValue}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {item.fromValue && (
                                      <>
                                        <span style={{
                                          display: "inline-block", padding: "1px 7px",
                                          background: fromStyle.bg, borderRadius: 4,
                                          fontSize: 11, color: fromStyle.text, fontWeight: 500,
                                        }}>
                                          {item.fromValue}
                                        </span>
                                        <span style={{ color: "#9ca3af", fontSize: 13 }}>→</span>
                                      </>
                                    )}
                                    {item.toValue && (
                                      <span style={{
                                        display: "inline-block", padding: "1px 7px",
                                        background: toStyle.bg, borderRadius: 4,
                                        fontSize: 11, color: toStyle.text, fontWeight: 500,
                                      }}>
                                        {item.toValue}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              {/* Reassign reason */}
                              {item.activityType === "REASSIGN" && item.action && item.action.includes("—") && (
                                <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", marginTop: 2 }}>
                                  "{item.action.split("—").slice(1).join("—").trim()}"
                                </div>
                              )}
                              {/* Comment quote text */}
                              {item.activityType === "COMMENT" && rawComment && (
                                isEditingThis ? (
                                  <div style={{ marginTop: 2, fontSize: 12, color: "#2563eb", fontStyle: "italic" }}>
                                    Editing this comment in the box above
                                  </div>
                                ) : (
                                  <div
                                    style={{ marginTop: 2, fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}
                                    dangerouslySetInnerHTML={{ __html: marked(rawComment, { breaks: true }) }}
                                  />
                                )
                              )}

                              {item.activityType === "COMMENT" && isMine && !isEditingThis && (
                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingCommentId(item.id);
                                      setNewComment(rawComment);
                                      setCommentTab("write");
                                      setTimeout(() => {
                                        const ta = document.getElementById("ticket-comment-editor");
                                        if (ta) { ta.focus(); ta.scrollIntoView({ behavior: "smooth", block: "center" }); }
                                      }, 0);
                                    }}
                                    style={{
                                      background: "none", border: "none", padding: 0,
                                      cursor: "pointer", color: "#2563eb", fontSize: 11, fontWeight: 600,
                                    }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                              {/* Comment attachments */}
                              {item.activityType === "COMMENT" && (item.attachments || []).length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
                                  {item.attachments.map((att) => (
                                    <button
                                      key={att.id}
                                      onClick={async () => {
                                        try {
                                          const res = await apiClient.get(`/tickets/attachments/id/${att.id}`, {
                                            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                                            responseType: "blob",
                                          });
                                          const url = window.URL.createObjectURL(new Blob([res.data]));
                                          const a = document.createElement("a");
                                          a.href = url; a.download = att.fileName;
                                          document.body.appendChild(a); a.click(); a.remove();
                                          window.URL.revokeObjectURL(url);
                                        } catch { alert("Failed to download attachment"); }
                                      }}
                                      style={{
                                        background: "#fff", border: "1px solid #d1d5db", borderRadius: 4,
                                        padding: "2px 7px", fontSize: 11, color: "#2563eb", cursor: "pointer",
                                      }}
                                    >
                                      📎 {att.fileName}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Time */}
                            <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                              </svg>
                              <span style={{ fontSize: 10, color: "#9ca3af" }}>
                                {item.createdAt
                                  ? (group.label === "OLDER"
                                    ? new Date(item.createdAt).toLocaleDateString([], {
                                      day: "2-digit", month: "short", year: "numeric",
                                    }) + " " + new Date(item.createdAt).toLocaleTimeString([], {
                                      hour: "2-digit", minute: "2-digit",
                                    })
                                    : new Date(item.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

        </div>{/* end body padding */}
      </div>{/* end scrollable */}

      {/* ── Bottom Action Bar ── */}
      <div style={{
        borderTop: "1px solid #e5e7eb",
        padding: "10px 14px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
      }}>
        {/* Change Status — primary blue */}
        <button
          onClick={() => onChangeStatus && onChangeStatus(ticket)}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "8px 14px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            flexShrink: 0,
            fontFamily: "inherit",
            transition: "background 0.15s",
          }}
          onMouseOver={e => e.currentTarget.style.background = "#1d4ed8"}
          onMouseOut={e => e.currentTarget.style.background = "#2563eb"}
        >
          Change Status
        </button>

        {/* Reassign */}
        <button
          onClick={() => onReassign && onReassign(ticket)}
          style={{
            background: "#fff",
            color: "#374151",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            padding: "7px 14px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            flexShrink: 0,
            fontFamily: "inherit",
            transition: "border-color 0.15s",
          }}
          onMouseOver={e => e.currentTarget.style.borderColor = "#9ca3af"}
          onMouseOut={e => e.currentTarget.style.borderColor = "#d1d5db"}
        >
          Reassign
        </button>

        {/* Edit */}
        <button
          onClick={() => onEdit && onEdit(ticket, canEdit)}
          style={{
            background: "#fff",
            color: "#374151",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            padding: "7px 14px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            flexShrink: 0,
            fontFamily: "inherit",
            transition: "border-color 0.15s",
          }}
          onMouseOver={e => e.currentTarget.style.borderColor = "#9ca3af"}
          onMouseOut={e => e.currentTarget.style.borderColor = "#d1d5db"}
        >
          Edit
        </button>

        {/* Close — right-aligned, with icon */}
        <button
          onClick={onClose}
          style={{
            marginLeft: "auto",
            background: "none",
            color: "#16a34a",
            border: "none",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "7px 4px",
            fontFamily: "inherit",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Close
        </button>
      </div>

    </div>
  );
};

export default TicketDetailPanel;